import {
  Group,
  Paper,
  Popover,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useUserClientContext } from '@/app/clientContext';
import { useGetReleaseDetailQuery } from '@/features/adminSlice';
import { ReleaseStatusEffective } from '@/generated/types/navigator/types.gen';
import { formatDateAsPerLocale } from '@/helpers/date';
import { useDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import { useReleaseId } from '@/hooks/useReleaseId';
import { getDeliveryIdentifiers } from './internalTools/tools/ReleaseList/releaseDetailUtils';
import { useClientTerminology } from './list/NetworkTerms';

interface BannerData {
  releaseId: string;
  status: ReleaseStatusEffective;
  createdAt: string;
  deliveryIdentifiers: string[];
  missingDelivery: string | null;
  clientCode: string;
}

const INTERNAL_STATUSES: ReleaseStatusEffective[] = [
  ReleaseStatusEffective.DRAFT,
  ReleaseStatusEffective.REVOKED,
  ReleaseStatusEffective.ARCHIVED,
];

/**
 * Which release the banner should describe. The URL is authoritative: an explicit
 * `release` selection wins. A delivery-only URL names no release, so we fall back
 * to the release the backend resolved that delivery to (its latest draft, or its
 * published release once that draft ships). Null hides the banner.
 */
const resolveBannerRelease = (
  urlReleaseId: string | null,
  deliveryIdentifier: string | undefined,
  resolvedReleaseId: string | null
): string | null => {
  if (urlReleaseId !== null) return urlReleaseId;
  return deliveryIdentifier !== undefined ? resolvedReleaseId : null;
};

const useInternalReleaseBanner = (): BannerData | null => {
  const { context, isInternalUser } = useUserClientContext();
  const { code: clientCode, releaseId: resolvedReleaseId } = context;
  const deliveryIdentifier = useDeliveryIdentifier();

  const releaseId = resolveBannerRelease(
    useReleaseId(),
    deliveryIdentifier,
    resolvedReleaseId
  );

  const { data: detail, isFetching: detailFetching } = useGetReleaseDetailQuery(
    { releaseId: releaseId ?? '' },
    { skip: !isInternalUser || !clientCode || releaseId === null }
  );

  if (!isInternalUser || !clientCode || releaseId === null) return null;
  if (detailFetching || !detail) return null;
  if (!INTERNAL_STATUSES.includes(detail.effective_status)) return null;

  const releaseDeliveries = getDeliveryIdentifiers(detail.batches);
  const scopedDeliveries = deliveryIdentifier
    ? releaseDeliveries.filter(
        (id) => id.toLowerCase() === deliveryIdentifier.toLowerCase()
      )
    : releaseDeliveries;
  const missingDelivery =
    deliveryIdentifier && scopedDeliveries.length === 0
      ? deliveryIdentifier
      : null;

  return {
    releaseId: detail.id,
    status: detail.effective_status,
    createdAt: detail.created_at,
    deliveryIdentifiers: scopedDeliveries,
    missingDelivery,
    clientCode,
  };
};

interface LabeledFieldProps {
  label: string;
  children: React.ReactNode;
}

const LabeledField = ({ label, children }: LabeledFieldProps) => (
  <Stack gap={0}>
    <Text size="10px" c="dimmed" lh={1.2}>
      {label}
    </Text>
    <Text size="xs" fw={500} c="white" lh={1.2} component="div">
      {children}
    </Text>
  </Stack>
);

interface DeliveriesFieldProps {
  identifiers: string[];
}

const DeliveriesField = ({ identifiers }: DeliveriesFieldProps) => {
  const [first, ...rest] = identifiers;
  return (
    <LabeledField label="Deliveries">
      <Group gap="xs" wrap="nowrap">
        <span>{first}</span>
        {rest.length > 0 && (
          <Popover position="bottom-end" withArrow shadow="md">
            <Popover.Target>
              <UnstyledButton
                data-testid="internal-release-banner-toggle"
                style={{
                  backgroundColor: 'var(--mantine-color-dark-4)',
                  color: 'var(--mantine-color-white)',
                  borderRadius: 'var(--mantine-radius-sm)',
                  padding: '1px 6px',
                  fontSize: '11px',
                  lineHeight: 1.2,
                  fontWeight: 500,
                }}
              >
                +{rest.length}
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown p="xs">
              <ScrollArea.Autosize mah={280}>
                <Stack gap={4} data-testid="internal-release-banner-deliveries">
                  {identifiers.map((id) => (
                    <Text key={id} size="sm">
                      {id}
                    </Text>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Popover.Dropdown>
          </Popover>
        )}
      </Group>
    </LabeledField>
  );
};

export const InternalReleaseBanner = () => {
  const data = useInternalReleaseBanner();
  const intl = useClientTerminology();

  if (!data) return null;

  return (
    <Paper
      radius="md"
      shadow="md"
      py={6}
      px="sm"
      aria-live="polite"
      data-testid="internal-release-banner"
      style={{ backgroundColor: 'rgba(33, 37, 41, 0.85)' }}
    >
      <Group gap="md" wrap="nowrap">
        <LabeledField label="Customer">{data.clientCode}</LabeledField>
        <LabeledField label="Date">
          {formatDateAsPerLocale(data.createdAt, undefined, {
            dateStyle: 'long',
          })}
        </LabeledField>
        <LabeledField label="Status">
          {intl.formatMessage({ id: `releaseStatusBadge.${data.status}` })}
        </LabeledField>
        {data.missingDelivery ? (
          <LabeledField label="Delivery">
            <Text
              size="xs"
              fw={500}
              c="yellow.4"
              lh={1.2}
              data-testid="internal-release-banner-missing-delivery"
            >
              {data.missingDelivery} is not in this release
            </Text>
          </LabeledField>
        ) : (
          data.deliveryIdentifiers.length > 0 && (
            <DeliveriesField identifiers={data.deliveryIdentifiers} />
          )
        )}
        <LabeledField label="Release ID">
          <Text size="xs" fw={500} c="white" lh={1.2} ff="monospace">
            {data.releaseId}
          </Text>
        </LabeledField>
      </Group>
    </Paper>
  );
};
